/**
 * Runtime Configuration Helper
 *
 * Purpose:
 *   Provides access to runtime configuration from backend (/server/config.js)
 *   Falls back to build-time environment variables for development
 *
 * Why:
 *   Allows same build to work across different environments (dev, staging, prod)
 *   Configuration loaded from backend at runtime via window._env_
 *
 * Usage:
 *   import { getRuntimeConfig } from '@/core/config/runtime.config';
 *   const config = getRuntimeConfig();
 *   const apiUrl = config.apiBaseUrl;
 */

interface RuntimeEnv {
  RTRT_APP_BASE_URL?: string;
  RTRT_APP_ALLOWED_AAD_GROUP?: string;
  RTRT_APP_REDIRECT_URI?: string;
  RTRT_APP_CLIENT_ID?: string;
  RTRT_APP_AUTHORITY?: string;
  RTRT_TARGET_GROUP_ID?: string;
}

// Extend Window interface to include runtime config
declare global {
  interface Window {
    _env_?: RuntimeEnv;
  }
}

export interface RuntimeConfig {
  apiBaseUrl: string;
  allowedAadGroup: string;
  redirectUri: string;
  azureClientId: string;
  azureAuthority: string;
  targetGroupId: string;
}

/**
 * Get runtime configuration from backend or fallback to build-time env vars
 *
 * Priority:
 *   1. window._env_ (from /server/config.js)
 *   2. import.meta.env (build-time Vite variables)
 *   3. Default values
 */
export const getRuntimeConfig = (): RuntimeConfig => {
  const runtimeEnv = window._env_ || {};

  return {
    apiBaseUrl:
      runtimeEnv.RTRT_APP_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      'http://localhost:8000',

    allowedAadGroup:
      runtimeEnv.RTRT_APP_ALLOWED_AAD_GROUP ||
      import.meta.env.VITE_ALLOWED_AAD_GROUP ||
      'development',

    redirectUri:
      runtimeEnv.RTRT_APP_REDIRECT_URI ||
      import.meta.env.VITE_AZURE_REDIRECT_URI ||
      window.location.origin,

    azureClientId:
      runtimeEnv.RTRT_APP_CLIENT_ID ||
      import.meta.env.VITE_AZURE_CLIENT_ID ||
      '',

    azureAuthority:
      runtimeEnv.RTRT_APP_AUTHORITY ||
      import.meta.env.VITE_AZURE_AUTHORITY ||
      '',

    targetGroupId:
      runtimeEnv.RTRT_TARGET_GROUP_ID ||
      import.meta.env.VITE_TARGET_GROUP_ID ||
      '',
  };
};

// Export singleton instance
export const runtimeConfig = getRuntimeConfig();
